import { injectDataIntoContent } from "directus-extension-flexible-editor/content";

export const usePublications = () => {

    const { getSingletonItem, getItems } = useDirectusItems();

    const getBookshopPage = async () => {
        const fetchPage = getSingletonItem<any>({
            collection: 'page_bookshop',
            params: {
                fields: [
                    'title',
                    'introduction',
                ],
            }
        })
        const fetchBooks = getItems<any>({
            collection: 'publications',
            params: {
                limit: -1,
                fields: [
                    'id',
                    'type',
                    'cover.id',
                    'cover.width',
                    'cover.height',
                    'cover.title',
                    'cover.blurhash',
                    'title',
                    'summary',
                    'publication_date',
                    'url',
                    'price_hardcopy',
                    'price_electronic_copy',
                ],
                filter: {
                    _and: [
                        { status: { _eq: 'published' } },
                        { type: { _eq: 'bookshop' } }
                    ]
                },
                meta: 'filter_count',
                sort: '-publication_date'
            }
        })

        const [page, books] = await Promise.all([fetchPage, fetchBooks])
        return {
            page,
            books
        }
    }

    const getBookshopBanner = async () => {

        const fetchBooks = getItems<any>({
            collection: 'publications',
            params: {
                limit: 5,
                fields: [
                    'id',
                    'type',
                    'cover.id',
                    'cover.width',
                    'cover.height',
                    'cover.title',
                    'cover.blurhash',
                ],
                filter: {
                    _and: [
                        { status: { _eq: 'published' } },
                        { type: { _eq: 'bookshop' } }
                    ]
                },
                sort: '-publication_date'
            }
        })

        const [books] = await Promise.all([fetchBooks])
        return {
            books
        }
    }

    const getBook = async (slug: any) => {
        const fetchBook = getItems<any>({
            collection: 'publications',
            params: {
                limit: -1,
                fields: [
                    'id',
                    'type',
                    'cover.id',
                    'cover.width',
                    'cover.height',
                    'cover.title',
                    'cover.blurhash',
                    'title',
                    'summary',
                    'publication_date',

                    'price_hardcopy',
                    'price_electronic_copy',
                    'isbn',
                    'binding',

                    'body',
                    'body_nodes.id',
                    'body_nodes.item:articles_nodes_image.image.id',
                    'body_nodes.item:articles_nodes_image.image.width',
                    'body_nodes.item:articles_nodes_image.image.height',
                    'body_nodes.item:articles_nodes_image.image.title',
                    'body_nodes.item:articles_nodes_image.image.description',
                    'body_nodes.item:articles_nodes_image.image.light_caption',
                    'body_nodes.item:articles_nodes_image.image.blurhash',
                    'body_nodes.item:articles_nodes_image.caption',
                    'body_nodes.item:articles_nodes_youtube.youtube_link',
                ],
                filter: {
                    _and: [
                        { status: { _eq: 'published' } },
                        { type: { _eq: 'bookshop' } },
                        { url: { _eq: slug } }
                    ]
                }
            }
        })

        const [books] = await Promise.all([fetchBook])

        if (books && books[0]) {
            injectDataIntoContent(books[0].body_nodes, books[0].body)
            delete books[0].body_nodes
        }

        return {
            page: books ? books[0] : null
        }
    }

    const getPositionPapersPage = async (p: number, filter: string | null) => {
        const fetchPage = getSingletonItem<any>({
            collection: 'page_position_papers',
            params: {
                fields: [
                    'title',
                    'introduction',
                ],
            }
        })
        const fetchPapers = getItems<any>({
            collection: 'publications',
            params: {
                limit: 8,
                page: p,
                fields: [
                    'id',
                    'type',
                    'cover.id',
                    'cover.width',
                    'cover.height',
                    'cover.title',
                    'cover.blurhash',
                    'title',
                    'publication_date',
                    'url',
                    'attachment.id',
                    'attachment.title'
                ],
                filter: {
                    _and: [
                        { status: { _eq: 'published' } },
                        { type: { _eq: 'position_paper' } },
                        { themes: { themes_id: { url: { _eq: filter } } } }
                    ]
                },
                meta: 'filter_count',
                sort: '-publication_date'
            }
        })
        const fetchThemes = getItems<any>({
            collection: 'themes',
            params: {
                limit: -1,
                fields: [
                    'title',
                    'url'
                ],
                filter: {
                    publications: {
                        _nnull: true
                    }
                }
            }
        })

        const [page, papers, themes] = await Promise.all([fetchPage, fetchPapers, fetchThemes])
        return {
            page,
            papers,
            themes
        }
    }

    const getPositionPapersBanner = async () => {

        const fetchPapers = getItems<any>({
            collection: 'publications',
            params: {
                limit: 5,
                fields: [
                    'id',
                    'type',
                    'cover.id',
                    'cover.width',
                    'cover.height',
                    'cover.title',
                    'cover.blurhash',
                ],
                filter: {
                    _and: [
                        { status: { _eq: 'published' } },
                        { type: { _eq: 'position_paper' } }
                    ]
                },
                sort: '-publication_date'
            }
        })

        const [papers] = await Promise.all([fetchPapers])
        return {
            papers
        }
    }

    const getAnnualReportsPage = async () => {
        const fetchPage = getSingletonItem<any>({
            collection: 'page_annual_reports',
            params: {
                fields: [
                    'title',
                    'introduction',
                ],
            }
        })

        const fetchReports = getItems<any>({
            collection: 'publications',
            params: {
                limit: -1,
                fields: [
                    'id',
                    'type',
                    'cover.id',
                    'cover.width',
                    'cover.height',
                    'cover.title',
                    'cover.blurhash',
                    'title',
                    'publication_date',
                    'url',
                    'attachment.id',
                    'attachment.title'
                ],
                filter: {
                    _and: [
                        { status: { _eq: 'published' } },
                        {
                            _or: [
                                { type: { _eq: 'annual_report' } },
                                { type: { _eq: 'annual_report_excerpt' } }
                            ]
                        }
                    ]
                },
                meta: 'filter_count',
                sort: '-publication_date'
            }
        })

        const [page, reports] = await Promise.all([fetchPage, fetchReports])
        return {
            page,
            reports
        }
    }

    const getAnnualReport = async (slug: any) => {
        const fetchReport = getItems<any>({
            collection: 'publications',
            params: {
                limit: -1,
                fields: [
                    'id',
                    'type',
                    'cover.id',
                    'cover.width',
                    'cover.height',
                    'cover.title',
                    'cover.blurhash',
                    'title',
                    'publication_date',

                    'body',
                    'body_nodes.id',
                    'body_nodes.item:articles_nodes_image.image.id',
                    'body_nodes.item:articles_nodes_image.image.width',
                    'body_nodes.item:articles_nodes_image.image.height',
                    'body_nodes.item:articles_nodes_image.image.title',
                    'body_nodes.item:articles_nodes_image.image.description',
                    'body_nodes.item:articles_nodes_image.image.light_caption',
                    'body_nodes.item:articles_nodes_image.image.blurhash',
                    'body_nodes.item:articles_nodes_image.caption',
                    'body_nodes.item:articles_nodes_youtube.youtube_link',

                    'attachment.id',
                    'attachment.title'
                ],
                filter: {
                    _and: [
                        { status: { _eq: 'published' } },
                        { type: { _eq: 'annual_report_excerpt' } },
                        { url: { _eq: slug } }
                    ]
                }
            }
        })

        const [reports] = await Promise.all([fetchReport])

        if (reports && reports[0]) {
            injectDataIntoContent(reports[0].body_nodes, reports[0].body)
            delete reports[0].body_nodes
        }

        return {
            page: reports ? reports[0] : null
        }
    }

    const getStatisticsPage = async () => {
        const fetchPage = getSingletonItem<any>({
            collection: 'page_statistics',
            params: {
                fields: [
                    'title',
                    'introduction',
                ],
            }
        })
        const fetchStatistics = getItems<any>({
            collection: 'publications',
            params: {
                limit: -1,
                fields: [
                    'id',
                    'type',
                    'cover.id',
                    'cover.width',
                    'cover.height',
                    'cover.title',
                    'cover.blurhash',
                    'title',
                    'publication_date',
                    'url',
                    'attachment.id',
                    'attachment.title'
                ],
                filter: {
                    _and: [
                        { status: { _eq: 'published' } },
                        { type: { _eq: 'statistic' } }
                    ]
                },
                meta: 'filter_count',
                sort: '-publication_date'
            }
        })

        const [page, statistics] = await Promise.all([fetchPage, fetchStatistics])
        return {
            page,
            statistics
        }
    }

    return {
        getBookshopPage,

        getBookshopBanner,
        getPositionPapersBanner,

        getBook,
        getPositionPapersPage,
        getAnnualReportsPage,
        getAnnualReport,
        getStatisticsPage
        
    }
}